<template>
  <div class="invoice-page">
    <!-- 实名认证拦截弹窗 -->
    <div class="auth-modal" v-if="showAuthModal">
      <div class="auth-modal-content">
        <h3 class="auth-modal-title">实名认证提醒</h3>
        <p class="auth-modal-desc">
          您尚未完成实名认证，无法进行开票相关操作。<br>
          请先完善实名认证信息，完成后即可正常开具发票。<br>
          如有疑问，欢迎随时联系我们。
        </p>
        <button class="auth-modal-btn" @click="goToAuthPage">去完善</button>
      </div>
    </div>

    <!-- 未认证提示 -->
    <div class="unauth-tip" v-if="!isAuthenticated">
      <div class="tip-content">
        <i class="tip-icon"></i>
        <p>请先完成实名认证，才能进行开票相关操作</p>
        <button class="tip-btn" @click="goToAuthPage">去实名认证</button>
      </div>
    </div>

    <!-- 已认证才显示核心内容 -->
    <div v-if="isAuthenticated">
      <!-- 发票管理说明区域 -->
      <div class="invoice-desc">
        <h3>发票管理</h3>
        <ul>
          <li>开票时间：无特殊或不可抗力情况下，我司将在5个工作日内为您开票。</li>
          <li>开票类型：默认开票类型为<span class="highlight">增值税普通发票</span>。企业申请开具<span class="highlight">增值税专用发票</span>，请联系客服办理。
          </li>
          <li>服务名称：开出的发票【货物或应税劳务、服务名称】一栏填写的是“<span class="highlight">*设计服务*数字模型服务费</span>”。
          </li>
          <li>开票税率：开出的发票金额为含税价格，税率6%。</li>
        </ul>
      </div>

      <!-- 原生选项卡区域 -->
      <div class="invoice-tabs">
        <!-- 选项卡头部（移除激活条） -->
        <div class="tabs-header">
          <div class="tab-item" :class="{ active: activeTab === 'order' }" @click="activeTab = 'order'">
            选择开票订单
          </div>
          <div class="tab-item" :class="{ active: activeTab === 'record' }" @click="activeTab = 'record'">
            开票记录
          </div>
        </div>

        <!-- 选项卡内容 -->
        <div class="tabs-content">
          <!-- 选择开票订单 -->
          <div v-show="activeTab === 'order'" class="tab-panel">
            <div class="table-container">
              <!-- 加载状态 -->
              <div v-if="loading" class="loading-text" style="text-align: center; padding: 40px 0;">
                加载中...
              </div>
              <!-- 原生表格 -->
              <table v-else class="native-table" border="1">
                <thead>
                  <tr>
                    <th>支付时间</th>
                    <th>订单号</th>
                    <th>模型标题</th>
                    <th>模型类型</th>
                    <!-- <th>订单状态</th> -->
                    <th>抽成金额</th>
                    <th>操作</th> <!-- 新增操作列 -->
                  </tr>
                </thead>
                <tbody>
                  <!-- 空状态 -->
                  <tr v-if="invoiceOrders.length === 0">
                    <td colspan="7" class="empty-cell">{{ emptyText }}</td> <!-- 列数改为7 -->
                  </tr>
                  <!-- 数据行 -->
                  <tr v-for="(item, index) in invoiceOrders" :key="index">
                    <td>{{ item.payTime }}</td>
                    <td>{{ item.merchantOrderNo }}</td>
                    <td>{{ item.title }}</td>
                    <td>{{ item.type }}</td>
                    <!-- <td>{{ item.orderStatus }}</td> -->
                    <td>{{ item.payAmount }}</td>
                    <td>
                      <!-- 开票按钮，禁用已开票/不可开票状态 -->
                      <button class="operate-btn invoice-btn" @click="openInvoiceDialog(item)">
                        申请开票
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <!-- 开票订单分页组件 - 每页5条 -->
            <div class="pagination-container" v-if="totalOrders > 0">
              <el-pagination @current-change="handleCurrentChange" :current-page="currentPage" :page-size="pageSize"
                layout="total, prev, pager, next, jumper" :total="totalOrders" background>
              </el-pagination>
            </div>
          </div>

          <!-- 开票记录 -->
          <div v-show="activeTab === 'record'" class="tab-panel">
            <div class="table-container">
              <!-- 加载状态 -->
              <div v-if="loading" class="loading-text" style="text-align: center; padding: 40px 0;">
                加载中...
              </div>
              <table v-else class="native-table" border="1">
                <thead>
                  <tr>
                    <th>商户订单号</th>
                    <th>标题</th>
                    <th>申请时间</th>
                    <th>发票抬头</th>
                    <th>发票总额</th>
                    <th>状态</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="invoiceRecords.length === 0">
                    <td colspan="6" class="empty-cell">{{ emptyRecordText }}</td>
                  </tr>
                  <tr v-for="(item, index) in invoiceRecords" :key="index">
                    <td>{{ item.orderId }}</td>
                    <td>{{ item.orderType }}</td>
                    <td>{{ item.applyTime }}</td>
                    <td>{{ item.invoiceTitle }}</td>
                    <td>{{ item.totalAmount }}</td>
                    <td>{{ item.status }}</td>
                  </tr>
                </tbody>
              </table>

              <!-- 申请开票按钮（空状态时显示） -->
              <div v-if="invoiceRecords.length === 0 && !loading" class="empty-btn-wrap">
                <button class="primary-btn" @click="activeTab = 'order'">申请开票</button>
              </div>
            </div>
            <!-- 开票记录分页组件 - 每页5条 -->
            <div class="pagination-container" v-if="totalRecords > 0">
              <el-pagination @current-change="handleCurrentChange" :current-page="currentPage" :page-size="pageSize"
                layout="total, prev, pager, next, jumper" :total="totalRecords" background>
              </el-pagination>
            </div>
          </div>
        </div>
      </div>

      <!-- 新增：开票申请弹窗 -->
      <div class="modal-overlay" v-if="invoiceDialogVisible" @click="closeInvoiceModal">
        <div class="modal-content" @click.stop style="width: 500px;">
          <div class="modal-header">
            <h3>申请开票</h3>
            <button class="close-btn" @click="invoiceDialogVisible = false">×</button>
          </div>
          <div class="modal-body">
            <div class="invoice-form">
              <!-- 订单信息展示 -->
              <div class="form-item">
                <label><span class="required">*</span>订单编号：</label>
                <span class="form-value">{{ currentOrder.merchantOrderNo || '-' }}</span>
              </div>
              <div class="form-item">
                <label><span class="required">*</span>订单金额：</label>
                <span class="form-value">{{ currentOrder.amount || '0.00' }} 元</span>
              </div>

              <!-- 抬头类型选择（单选） -->
              <div class="form-item">
                <label><span class="required">*</span>抬头类型：</label>
                <div class="radio-group">
                  <label class="radio-item">
                    <input type="radio" v-model="invoicePersonType" value="个人" />
                    个人
                  </label>
                  <label class="radio-item">
                    <input type="radio" v-model="invoicePersonType" value="企业" />
                    企业
                  </label>
                </div>
              </div>
              <!-- 开票类型选择（可选） -->
              <div class="form-item">
                <label><span class="required">*</span>开票类型：</label>
                <select v-model="invoiceType" class="form-select">
                  <option value="增值税普通发票">增值税普通发票</option>
                  <option value="增值税专用发票">增值税专用发票</option>
                </select>
              </div>

              <!-- 专票提示 + 二维码（仅企业+专票显示） -->
              <div v-if="invoicePersonType === '企业' && invoiceType === '增值税专用发票'" class="special-invoice-tip">
                <p>如需开具增值税专用发票，请联系客服办理</p>
                <img src="../../../assets/erweima.png" alt="客服二维码" class="qrcode-img">
              </div>

              <!-- 发票抬头（专票不显示） -->
              <div class="form-item" v-if="!(invoicePersonType === '企业' && invoiceType === '增值税专用发票')">
                <label><span class="required">*</span>发票抬头：</label>
                <input v-model="invoiceTitle" class="form-input" placeholder="企业请输入公司名称，个人请输入姓名" />
              </div>

              <!-- 纳税人识别号（仅企业+普票显示） -->
              <div class="form-item" v-if="invoicePersonType === '企业' && invoiceType !== '增值税专用发票'">
                <label><span class="required">*</span>纳税人识别号：</label>
                <input v-model="taxpayerId" class="form-input" placeholder="请输入纳税人识别号" />
              </div>

              <!-- 联系电话（专票不显示） -->
              <div class="form-item" v-if="!(invoicePersonType === '企业' && invoiceType === '增值税专用发票')">
                <label><span class="required">*</span>联系电话：</label>
                <input v-model="phone" class="form-input" placeholder="请输入联系电话" />
              </div>

              <!-- 收票邮箱（专票不显示） -->
              <div class="form-item" v-if="!(invoicePersonType === '企业' && invoiceType === '增值税专用发票')">
                <label><span class="required">*</span>收票邮箱：</label>
                <input v-model="email" class="form-input" placeholder="请输入收票邮箱" />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="cancel-btn" @click="invoiceDialogVisible = false">取消</button>
            <button class="confirm-btn" @click="submitInvoiceApply" :disabled="!isSubmitBtnEnabled">
              提交申请
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InvoicePage',
  computed: {
    isSubmitBtnEnabled() {
      // 企业 + 专票 → 直接禁用提交按钮
      if (this.invoicePersonType === '企业' && this.invoiceType === '增值税专用发票') {
        return false;
      }

      // 未选择抬头类型 → 禁用
      if (['', undefined, null].includes(this.invoicePersonType)) return false;
      // 未填写发票抬头 → 禁用
      if (!this.invoiceTitle) return false;
      // 企业普票未填税号 → 禁用
      if (this.invoicePersonType === '企业' && !this.taxpayerId) return false;
      // 未填电话 → 禁用
      if (!this.phone) return false;
      // 未填邮箱 → 禁用
      if (!this.email) return false;

      return true;
    }
  },
  data() {
    return {
      // 实名认证相关
      isAuthenticated: null,
      showAuthModal: null,
      // 选项卡激活态
      activeTab: 'order',
      // 表格数据
      invoiceOrders: [],
      invoiceRecords: [],
      // 总数
      totalOrders: 0,
      totalRecords: 0,
      // 空状态文本
      emptyText: '当前没有可开票的订单。',
      emptyRecordText: '你还没有开票记录~',
      // 弹窗状态
      invoiceDialogVisible: false, // 开票弹窗
      // 分页参数 - 每页5条
      currentPage: 1,
      pageSize: 5,  // 修改为每页5条数据
      // 加载状态
      loading: false,
      // 新增：开票相关
      currentOrder: {}, // 当前选择的开票订单
      invoiceType: '增值税普通发票', // 默认开票类型
      invoicePersonType: '个人', // 抬头类型（个人/企业）
      invoiceTitle: '', // 发票抬头
      taxpayerId: '', // 纳税人识别号
      phone: '', // 联系电话
      email: '' // 收取邮箱
    };
  },
  watch: {
    activeTab() {
      // 切换选项卡时重置分页并重新获取数据
      this.currentPage = 1;
      this.fetchData();
    },
    // 监听抬头类型/开票类型变化，清空表单
    invoicePersonType() {
      this.clearForm();
    },
    invoiceType() {
      this.clearForm();
    }
  },
  async mounted() {
    // 先检查实名认证状态
    await this.checkAuthStatus();
    // 已认证则加载数据
    if (this.isAuthenticated === true) {
      this.fetchData();
    }
  },
  methods: {
    // 清空表单
    clearForm() {
      this.invoiceTitle = '';
      this.taxpayerId = '';
      this.phone = '';
      this.email = '';
    },
    // 检查实名认证状态（复用参考代码逻辑）
    async checkAuthStatus() {
      try {
        const res = await this.$apiFun.getSeleMerchantCA();
        if (res.status === 200 || res.code === 200) {
          this.isAuthenticated = !!res.obj?.merchantCA;
          this.showAuthModal = !this.isAuthenticated;
        } else {
          this.$message?.error("认证状态查询失败");
          this.isAuthenticated = false;
          this.showAuthModal = true;
        }
      } catch (err) {
        this.$message?.error('认证状态查询失败');
        console.error('查询认证状态出错：', err);
        this.isAuthenticated = false;
        this.showAuthModal = true;
      }
    },
    // 跳转到实名认证页面
    goToAuthPage() {
      this.showAuthModal = false;
      this.$router?.push('/dashboard/seller/info');
    },
    // 分页-每页条数改变（预留）
    handleSizeChange(val) {
      this.pageSize = val;
      this.fetchData();
    },
    // 分页-当前页改变（订单/记录共用）
    handleCurrentChange(val) {
      this.currentPage = val;
      this.fetchData();
    },
    // 获取订单/记录数据（增加未认证拦截）
    async fetchData() {
      // 未认证拦截
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }

      this.loading = true;
      try {
        if (this.activeTab === 'order') {
          // 选择开票订单接口
          const res = await this.$apiFun.getInvoiceOrder({
            type: 1,
            pageIndex: this.currentPage,
            pageSize: this.pageSize // 使用每页5条的参数
          });
          if (res.status === 200) {
            const orderList = Array.isArray(res.obj) ? res.obj : (res.obj?.list || []);
            this.invoiceOrders = orderList.map(item => ({
              payTime: item.paymentTime || '-',
              merchantOrderNo: item.orderid || '-',
              title: item.orderType || '-',
              type: item.purchaseType || '-',
              orderStatus: item.transactionStatus || '-',
              payAmount: item.amountPaid || '0.00',
              amount: item.amount || '0.00'
            }));
            // 正确获取总条数（从接口返回的总条数字段，而非当前列表长度）
            this.totalOrders = res.num || res.total || this.invoiceOrders.length;
          } else {
            this.$message.error(`获取订单列表失败: ${res.msg || '接口异常'}`);
            this.invoiceOrders = [];
            this.totalOrders = 0;
          }
        } else if (this.activeTab === 'record') {
          // 开票记录接口
          const res = await this.$apiFun.getInvoiceRecord({
            pageIndex: this.currentPage,
            pageSize: this.pageSize // 使用每页5条的参数
          });
          if (res.status === 200) {
            const recordList = Array.isArray(res.obj) ? res.obj : (res.obj?.list || []);
            this.invoiceRecords = recordList.map(item => ({
              orderId: item.orderId || '-',
              orderType: item.orderType || '-',
              applyTime: item.applyTime || '-',
              invoiceTitle: item.invoiceTitle || '-',
              totalAmount: item.amount || '0.00',
              status: item.invoiceStatus || '未知'
            }));
            // 正确获取总条数
            this.totalRecords = res.num || res.total || this.invoiceRecords.length;
          } else {
            this.$message.error(`获取开票记录失败: ${res.msg || '接口异常'}`);
            this.invoiceRecords = [];
            this.totalRecords = 0;
          }
        }
      } catch (error) {
        this.$message.error(`获取数据失败: ${error.message || '网络错误'}`);
        console.error('获取数据出错:', error);
        // 异常时清空对应列表
        if (this.activeTab === 'order') {
          this.invoiceOrders = [];
          this.totalOrders = 0;
        }
        if (this.activeTab === 'record') {
          this.invoiceRecords = [];
          this.totalRecords = 0;
        }
      } finally {
        this.loading = false;
      }
    },
    // 新增：打开开票弹窗（增加未认证拦截）
    openInvoiceDialog(order) {
      // 未认证拦截
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }

      this.currentOrder = { ...order };
      // 重置所有表单字段
      this.invoicePersonType = '个人';
      this.invoiceType = '增值税普通发票';
      this.clearForm();
      this.invoiceDialogVisible = true;
    },
    // 关闭开票弹窗
    closeInvoiceModal() {
      this.invoiceDialogVisible = false;
      this.currentOrder = {};
    },
    // 新增：提交开票申请（增加未认证拦截）
    async submitInvoiceApply() {
      // 未认证拦截
      if (!this.isAuthenticated) {
        this.$message?.warning('请先完成实名认证');
        this.showAuthModal = true;
        return;
      }

      try {
        // 构建开票申请参数
        const params = {
          type: 1,
          orderId: this.currentOrder.merchantOrderNo,
          invoiceType: this.invoiceType,
          amount: this.currentOrder.amount,
          invoicePersonType: this.invoicePersonType,
          invoiceTitle: this.invoiceTitle,
          taxpayerId: this.taxpayerId || '',
          phone: this.phone,
          email: this.email
        };
        console.log(params);
        // 调用开票申请接口（需根据实际接口调整）
        const res = await this.$apiFun.postInvoiceRequest(params);
        if (res.status === 200) {
          this.$message.success('开票申请提交成功，我们将尽快处理');
          this.invoiceDialogVisible = false;
          // 刷新开票记录和订单列表
          this.activeTab = 'record';
          this.currentPage = 1;
          this.fetchData();
        } else {
          this.$message.error(`开票申请失败: ${res.msg || '接口异常'}`);
        }
      } catch (error) {
        this.$message.error(`开票申请失败: ${error.message || '网络错误'}`);
        console.error('提交开票申请出错:', error);
      }
    },
    // 模拟链接点击
    showQualification() {
      alert('展示如何出具资质的相关信息');
    },
    showInvoiceSample() {
      alert('展示发票样例');
    }
  }
};
</script>

<style lang="less" scoped>
// 深色主题变量
@bg-primary: transparent;
@bg-secondary: transparent;
@bg-tertiary: #454545;
@text-primary: #e0e0e0;
@text-secondary: #a0a0a0;
@accent-red: #ff4d4f;
@border-color: #444;
@highlight-color: #0ea5e9;
@accent-blue: #0ea5e9; // 分页组件主色

.invoice-page {
  color: @text-primary;
  min-height: 100vh;
  padding: 20px;
  box-sizing: border-box;
  background-color: @bg-primary;

  // 实名认证弹窗（复用参考代码样式）
  .auth-modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;

    .auth-modal-content {
      background-color: @bg-secondary;
      border-radius: 8px;
      width: 400px;
      padding: 30px 20px;
      text-align: center;

      .auth-modal-title {
        font-size: 20px;
        color: @text-primary;
        margin-bottom: 15px;
        font-weight: 500;
      }

      .auth-modal-desc {
        font-size: 14px;
        color: @text-secondary;
        line-height: 1.8;
        margin-bottom: 20px;
      }

      .auth-modal-btn {
        background-color: @accent-blue;
        color: #fff;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 16px;
        cursor: pointer;
        width: 100%;
        max-width: 200px;
      }
    }
  }

  // 未认证提示（复用参考代码样式）
  .unauth-tip {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 400px;
    background-color: @bg-secondary;
    border-radius: 6px;
    padding: 20px;
    margin-top: 20px;

    .tip-content {
      text-align: center;

      .tip-icon {
        display: inline-block;
        width: 60px;
        height: 60px;
        background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024"><path fill="%230ea5e9" d="M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372z"/><path fill="%230ea5e9" d="M512 140c-205.4 0-372 166.6-372 372s166.6 372 372 372 372-125.4 372-372-125.4-280-280-280zm0 508c-125.9 0-228-102.1-228-228s102.1-228 228-228 228 102.1 228 228-102.1 228-228 228z"/></svg>') no-repeat center;
        margin-bottom: 20px;
      }

      p {
        font-size: 16px;
        color: @text-secondary;
        margin-bottom: 20px;
      }

      .tip-btn {
        background-color: @accent-blue;
        color: #fff;
        border: none;
        padding: 10px 20px;
        border-radius: 4px;
        font-size: 14px;
        cursor: pointer;
      }
    }
  }

  // 发票说明区域
  .invoice-desc {
    padding: 15px;
    border-radius: 6px;
    margin-bottom: 20px;
    background-color: @bg-secondary;

    h3 {
      margin: 0 0 10px 0;
      font-size: 18px;
    }

    ul {
      margin: 0;
      padding-left: 20px;
      font-size: 14px;
      line-height: 24px;

      .highlight {
        color: @highlight-color;
      }

      .link-btn {
        color: @highlight-color;
        background: none;
        border: none;
        padding: 0;
        cursor: pointer;
        text-decoration: underline;
        font-size: 14px;
      }
    }
  }

  // 原生选项卡样式（移除激活条）
  .invoice-tabs {
    border-radius: 6px;
    padding: 15px;
    background-color: @bg-secondary;

    .tabs-header {
      display: flex;
      border-bottom: 1px solid @border-color;
      margin-bottom: 15px;

      .tab-item {
        padding: 8px 16px;
        cursor: pointer;
        color: @text-secondary;
        transition: color 0.2s;

        &.active {
          color: #ffffff; // 选中的选项卡文字为白色
          font-weight: 500;
        }
      }
    }

    .tabs-content {
      .tab-panel {
        width: 100%;
      }
    }
  }

  // 表格容器
  .table-container {
    border-radius: 6px;
    padding: 15px;
    margin-bottom: 15px;
    background-color: @bg-secondary;

    // 原生表格样式
    .native-table {
      width: 100%;
      border-collapse: collapse;
      background-color: transparent; // 表格透明

      th {
        background-color: rgba(58, 58, 58, 0.5);
        color: @text-primary;
        padding: 12px 8px;
        text-align: left;
        border-color: @border-color;
      }

      td {
        padding: 12px 8px;
        border-color: @border-color;
        background-color: transparent; // 单元格透明
        color: @text-primary;
      }

      tbody tr:hover {
        background-color: rgba(69, 69, 69, 0.6); // 悬浮半透明
      }

      .empty-cell {
        text-align: center;
        padding: 40px 0;
        color: @text-secondary;
        background-color: transparent;
      }
    }

    // 按钮容器（申请开票/添加发票抬头共用，居中显示）
    .empty-btn-wrap {
      text-align: center;
      padding: 20px 0;
    }

    // 加载文本样式
    .loading-text {
      color: @text-secondary;
      font-size: 14px;
    }
  }

  // 按钮样式
  .primary-btn {
    background-color: @highlight-color;
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;

    &:hover {
      background-color: #0b8bcd;
    }
  }

  .operate-btn {
    background: none;
    border: none;
    padding: 4px 8px;
    cursor: pointer;
    font-size: 12px;
    border-radius: 4px;

    &.view-btn {
      color: @highlight-color;
    }

    &.edit-btn {
      color: @highlight-color;
      margin-right: 8px;
    }

    &.delete-btn {
      color: @accent-red;
    }

    &.invoice-btn {
      color: white;
      background-color: @highlight-color;
      padding: 6px 12px;

      &:disabled {
        background-color: @bg-tertiary;
        color: @text-secondary;
        cursor: not-allowed;
      }
    }
  }

  // 原生弹窗样式
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;

    .modal-content {
      width: 500px;
      background-color: #1a1a1a;
      border-radius: 6px;
      box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);

      .modal-header {
        padding: 15px;
        border-bottom: 1px solid @border-color;
        display: flex;
        justify-content: space-between;
        align-items: center;

        h3 {
          margin: 0;
          color: @text-primary;
        }

        .close-btn {
          background: none;
          border: none;
          color: @text-secondary;
          font-size: 20px;
          cursor: pointer;
          width: 30px;
          height: 30px;
          display: flex;
          align-items: center;
          justify-content: center;

          &:hover {
            color: white;
          }
        }
      }

      .modal-body {
        padding: 15px;

        .invoice-form {
          .form-item {
            margin-bottom: 15px;

            label {
              display: inline-block;
              width: 120px;
              color: @text-primary;
              font-size: 14px;
            }

            .required {
              color: @accent-red;
              margin-right: 4px;
            }

            .radio-group {
              display: flex;
              gap: 20px;
              margin: 0 0 0 120px;
            }

            .radio-item {
              display: flex;
              align-items: center;
              gap: 6px;
              color: @text-primary;
              font-size: 14px;
              cursor: pointer;
            }

            .form-input,
            .form-select {
              width: calc(100% - 120px);
              padding: 8px;
              background-color: @bg-tertiary;
              border: 1px solid @border-color;
              border-radius: 4px;
              color: @text-primary;
              box-sizing: border-box;
            }

            .form-value {
              color: @text-primary;
              margin-left: 8px;
            }

            .no-info-tip {
              color: @highlight-color;
              font-size: 12px;
              margin-top: 8px;
              margin-left: 120px;

              a {
                color: @highlight-color;
                text-decoration: underline;
                cursor: pointer;
              }
            }
          }

          // 专票提示样式
          .special-invoice-tip {
            text-align: center;
            padding: 20px 0;
            margin-bottom: 15px;

            p {
              color: @text-primary;
              font-size: 16px;
              margin-bottom: 15px;
            }

            .qrcode-img {
              width: 180px;
              height: 180px;
              object-fit: contain;
              border: 1px solid @border-color;
              padding: 5px;
              background: #fff;
            }
          }
        }
      }

      .modal-footer {
        padding: 15px;
        border-top: 1px solid @border-color;
        display: flex;
        justify-content: flex-end;
        gap: 10px;

        .cancel-btn {
          background-color: @bg-tertiary;
          color: @text-primary;
          border: 1px solid @border-color;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;

          &:hover {
            background-color: @bg-secondary;
          }
        }

        .confirm-btn {
          background-color: @highlight-color;
          color: white;
          border: none;
          padding: 8px 16px;
          border-radius: 4px;
          cursor: pointer;

          &:hover {
            background-color: #0b8bcd;
          }

          &:disabled {
            background-color: @bg-tertiary;
            color: @text-secondary;
            cursor: not-allowed;
          }
        }
      }
    }
  }

  // 分页组件样式 - 适配深色主题
  .pagination-container {
    margin-top: 20px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 10px 0;
  }

  // 核心：修改分页组件的背景色、字体色，适配深色主题
  :deep(.el-pagination) {
    background-color: transparent !important;

    .el-pagination__total {
      color: #e0e0e0 !important;
      font-size: 14px !important;
    }

    .el-pager {
      li {
        background-color: #080a12 !important;
        color: #a0a0a0 !important;
        border: 1px solid #424242 !important;
        margin: 0 2px !important;

        &:hover {
          background-color: rgba(255, 255, 255, 0.1) !important;
          color: @accent-blue !important;
          border-color: @accent-blue !important;
        }

        &.active {
          background-color: @accent-blue !important;
          color: #ffffff !important;
          border-color: @accent-blue !important;
        }
      }
    }

    .btn-prev,
    .btn-next {
      background-color: @bg-secondary !important;
      color: @text-secondary !important;
      border: 1px solid @border-color !important;
      min-width: 30px !important;
      margin: 0 2px !important;

      &.disabled {
        background-color: darken(@bg-secondary, 5%) !important;
        color: darken(@text-secondary, 20%) !important;
        border-color: darken(@border-color, 10%) !important;
        cursor: not-allowed !important;

        &:hover {
          background-color: darken(@bg-secondary, 5%) !important;
          color: darken(@text-secondary, 20%) !important;
          border-color: darken(@border-color, 10%) !important;
        }
      }

      &:not(.disabled):hover {
        color: @accent-blue !important;
        border-color: @accent-blue !important;
      }

      .el-icon {
        font-size: 12px !important;
        font-weight: bold !important;
      }
    }

    .el-pagination__jump {
      color: #a0a0a0 !important;

      .el-input__inner {
        background-color: #080a12 !important;
        color: #e0e0e0 !important;
        border: 1px solid #424242 !important;

        &:focus {
          border-color: @accent-blue !important;
        }
      }
    }
  }
}
</style>